import * as PIXI from 'pixi.js';
import * as Sentry from '@sentry/browser';
import isEqual from 'lodash/isEqual';

import uniq from 'lodash/uniq';
import type { BufferingMode } from '../../enums';
import { CreationType, DynamicStylingCreationLevel, SeekMode, ValueMapMode } from '../../enums';
import { distance, unicodeToChar } from '../../libs/other';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import { BaseAsset } from '../asset/BaseAsset';
import { Container } from '../asset/Container';
import { Config } from '../../video-data/config';
import {
    assertArray,
    assertBoolean,
    assertInArray,
    assertInEnum,
    assertMinimum,
    assertNumber,
    assertNumberBetween,
    assertString
} from '../../validators';

import type { AssetTemplateReferenceMap, Coordinate, DestroyOptions, TextEditingOptions } from '../../types';
import { getAllTemplateAttributes } from '../attributes';
import { isTemplateReference } from '../attributes/template/common';
import type { DynamicAnimationConfiguration } from '../../libs/DynamicAnimationConfiguration';
import type { DynamicGraphicsConfigurator } from '../../libs/DynamicGraphicsConfigurator';
import { TextAssetMetrics } from './TextAssetMetrics';
import WordElementDataStore from './WordElementDataStore';
import { TextWord } from './TextWord';
import { HighlightSpan } from './HighlightSpan';
import { getGlobalFontManager } from './FontManager';
import { TextEditingManager } from './TextEditingManager';
import { getCharRangeFromSelection } from './TextEditingUtilities';
import { getTokenList } from './textMetricsHelpers';
import { TextLine } from './TextLine';
import { HighlightLine } from './HighlightLine';
import { TextAssetStyle } from './TextAssetStyle';
import { WordElementToken } from './WordElementToken';
import { WordElementPlaceholder } from './WordElementPlaceholder';
import type { WordStyle } from './WordStyle';

type Location = {
    /**
     * character index of the current text value
     */
    index: number;
    /**
     * distance between coordinate and the character specific by the index
     */
    distance: number;
};

export type UnsupportedFontInfo = {
    /**
     * The human readable font name
     */
    fontName: string;
    /**
     * URL of the font
     */
    fontUrl: string;
    /**
     * Part of the text that can't be rendered with the selected fonts
     */
    missing: string;
};

const DefaultTextEditingOptions = {
    minSize: 40,
};

/**
 * A set of properties that exist on the normal and highlight styles that can be
 * edited by the user via controls exposed in the video creator
 */
type TextEditingStyles = {
    /**
     * A localized value relative to the video's aspect ratio and other configuration
     */
    fontSize?: number;
};

export type FittedFontSize = {
    /**
     * The real font size
     */
    fontSize: number;
    /**
     * The equivalent base value font size (on 1080p)
     */
    baseFontSize: number;
    /**
     * If font size can fit all of the text
     */
    displaysAllText: boolean;
};

/**
 * TextAsset is a primitive text object with some convenience methods to easily create text
 * objects. If the text should animate or move, take a look at TimeAwareTextAsset.
 *
 * It's important that this class manages when text metrics should be recomputed. Certain styles,
 * such as color, do not seem to affect metrics but still require a metrics recalculation. The
 * reason being that the style gets cloned whenever metrics are generated and rendering
 * uses the style from the metrics.
 *
 * EVENTS TEXT ASSET SENDS
 * - assetCanPlay:  TextAsset is now playable.  This means that the font has loaded. We NEED the font to load or we
 *                  cannot draw text, so any failure to load will result in the event never being emitted. Emitted
 *                  on the SLIDE.
 *
**/
export class TextAsset extends BaseAsset {
    override _classType = 'TextAsset';

    #leadingMultiplier = 0.1;
    #text = '';
    #textSize = 80;
    #textScaleFactor = 1;
    #highlightAreas: [number, number][] = [];
    #highlightedTextScale = 1.0;
    #textMetrics: TextAssetMetrics = null;
    #textEditingManager: TextEditingManager = null;

    // font loading
    #fontManager = getGlobalFontManager();

    /**
     * The data store used to cache word element sprites.
     */
    #wordElementDataStore: WordElementDataStore;

    #wordWrap = false;

    #wordWrapWidth = Infinity;

    /**
     * Map of styles by name, currently normalStyle and highlightStyle
     */
    #styles: { normalStyle: TextAssetStyle; highlightStyle: TextAssetStyle; };

    /**
     * The container used for moving the entire text asset.
     *
     * @protected
     */
    _moveContainer: BaseAsset = null;

    /**
     * The container used for scaling the entire text asset.
     *
     * @protected
     */
    _scaleContainer: BaseAsset = null;

    /**
     * The container used to negate the scaling so it's children can be positioned
     * positioned in relation to the move container where {x: 0, y:0} is the top left
     * position of the move container.
     *
     * @protected
     */
    _negateScaleOffsetContainer: BaseAsset = null;

    /**
     * The container containing the built hierarchy. It is a child of the `_negateScaleOffsetContainer`.
     * When webgl is used, this container is not visible, and only used for the hierarchy.
     * @see _layersContainer
     */
    _hierarchyContainer: BaseAsset = null;

    /**
     * The container containing the render layers separating the graphics from the text.
     * When webgl is used, this is the displayed container.
     * @see _hierarchyContainer
     */
    _layersContainer: Container = null;

    /**
     * The list of all `TextLine` objects.
     *
     * [[word word word] [word word]]
     */
    #textLines: TextLine[] = [];

    /**
     * The list of all `HighlightLine` objects.
     */
    _highlightLines = [];

    /**
     * The layer into which all graphics are rendered. It is a child of the `_layersContainer`.
     */
    #graphicsLayer: PIXI.Sprite;
    #graphicsLayerRenderTexture: PIXI.RenderTexture;

    /**
     * The layer into which all text is rendered. It is a child of the `_layersContainer`.
     */
    #textLayer: PIXI.Sprite;
    #textLayerRenderTexture: PIXI.RenderTexture;

    /**
     * Optimization for `get _textWords`.
     */
    _cachedTextWords = null;

    /**
     * Optimization for `get _wordElementSprites`.
     */
    _cachedWordElementSprites = null;

    /**
     * Optimization for `get _highlightSpans`.
     */
    _cachedHighlightSpans = null;

    /**
     * Text editing options min_size max_size and defaultTextSizes from template
     */
    _textEditingOptions = null;

    constructor(container, settings: BaseAssetSettings) {
        super(container, settings);

        this.#wordElementDataStore = new WordElementDataStore(this.referenceToOutputMultiplier);

        // Autoupdate is false at first, as we want both styles to be fully constructed before any updates are sent.
        const normalStyle = new TextAssetStyle(
            this.#getNormalStyleCreationContainersAtLevelForType,
            { parent: this, redrawParentOnUpdate: true, autoUpdate: false }
        );
        const highlightStyle = new TextAssetStyle(
            this.#getHighlightStyleCreationContainersAtLevelForType,
            { parent: this, redrawParentOnUpdate: true, autoUpdate: false }
        );
        this.#styles = { normalStyle, highlightStyle };

        highlightStyle.textColor = 0xffffff;

        // These defaults ensure that old templates work as before, as well as being a good starting point.
        normalStyle.renderHighlighted = true;
        normalStyle.renderHighlightedColor = true;
        highlightStyle.renderHighlighted = false;
        // True by default, as we always want the color when `renderHighlighted` is true for the highlight layer.
        highlightStyle.renderHighlightedColor = true;

        normalStyle.autoUpdate = true;
        highlightStyle.autoUpdate = true;

        // Set default textEditingOptions
        this._textEditingOptions = DefaultTextEditingOptions;

        // If max text widths or height haven't been set by the default style, limit
        // those to the slide dimensions
        if (this.maxHeight === Infinity) {
            this.maxHeight = this.root.referenceResolution.height;
        }
        if (this.maxWidth === Infinity) {
            this.maxWidth = this.root.referenceResolution.width;
        }

        // hierarchy
        this._moveContainer = new BaseAsset(container, {
            updateParentOnRedraw: true,
            autoDraw: settings.autoDraw,
            name: this.name + '_move',
            fetchResources: false,
        });
        this._addInternalChild(this._moveContainer._pixiContainer);

        // add transform to move anchor to the center of the asset so we can scale from the center
        this._scaleContainer = new BaseAsset(container, {
            updateParentOnRedraw: true,
            autoDraw: settings.autoDraw,
            name: this.name + '_scale',
            fetchResources: false,
        });
        this._moveContainer._addInternalChild(this._scaleContainer._pixiContainer);

        // add transform to counter the effect of the parent's transform
        this._negateScaleOffsetContainer = new BaseAsset(container, {
            updateParentOnRedraw: true,
            autoDraw: settings.autoDraw,
            name: this.name + '_negateScale',
            fetchResources: false,
        });
        this._scaleContainer._addInternalChild(this._negateScaleOffsetContainer._pixiContainer);

        this._hierarchyContainer = new BaseAsset(container, {
            updateParentOnRedraw: true,
            autoDraw: settings.autoDraw,
            name: this.name + '_hierarchy',
            fetchResources: false,
        });
        this._negateScaleOffsetContainer._addInternalChild(this._hierarchyContainer._pixiContainer);

        this._layersContainer = new Container({
            container,
            name: this.name + '_layers',
        });
        this._addInternalChild(this._layersContainer._pixiContainer);

        const referenceResolution = container.root.referenceResolution;
        const outputResolution = container.root.outputResolution;
        const renderScale = {
            x: referenceResolution.width / outputResolution.width,
            y: referenceResolution.height / outputResolution.height,
        };

        this.#graphicsLayerRenderTexture = PIXI.RenderTexture.create(outputResolution);
        this.#graphicsLayer = new PIXI.Sprite(this.#graphicsLayerRenderTexture);
        this.#graphicsLayer.scale = renderScale;
        this.#graphicsLayer.name = 'graphicsLayer';

        this.#textLayerRenderTexture = PIXI.RenderTexture.create(outputResolution);
        this.#textLayer = new PIXI.Sprite(this.#textLayerRenderTexture);
        this.#textLayer.scale = renderScale;
        this.#textLayer.name = 'textLayer';

        this._layersContainer.addChild(this.#graphicsLayer);
        this._layersContainer.addChild(this.#textLayer);
    }

    /**
     * This is the same as PIXI's `calculateBounds()` except that it skips our render layers.
     */
    override calculateBounds() {
        try {
            this._layersContainer.visible = false;
            super.calculateBounds();
        }
        finally {
            this._layersContainer.visible = true;
        }
    }

    /** @inheritDoc */
    override hasTemplateReferenceValues() {
        return this.normalStyle.hasTemplateReferenceValues() || this.highlightStyle.hasTemplateReferenceValues();
    }

    // This override adds mappings for colors and fonts from `normalStyle` and `highlightStyle`.
    override buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        let result = super.buildTemplateReferenceMap();

        const getTemplateReferencesForStyle = (style: 'normalStyle' | 'highlightStyle') => {
            let styleResult = {};
            const attributes = getAllTemplateAttributes(this[style], ValueMapMode.GET_REFERENCE_VALUES);
            attributes.forEach(attr => {
                const attrKey = `${style}.${attr}`;
                styleResult[attrKey] = this[style][`_${attr}Value`].replace('themeRef_', '');
            });

            styleResult = {
                ...styleResult,
                ...this.#buildValueMapForStyle(ValueMapMode.GET_REFERENCE_VALUES, style),
            };
            // get dynamic styling properties for the style
            this[style].colorGraphics.forEach((graphic, index) => {
                const attrKeyPrefix = `${style}.colorGraphics.${index}.shape`;
                const templateAttributes = getAllTemplateAttributes(graphic.shape, ValueMapMode.GET_REFERENCE_VALUES);
                templateAttributes.forEach(attr => {
                    const attrKey = `${attrKeyPrefix}.${attr}`;
                    styleResult[attrKey] = graphic.shape[`_${attr}Value`].replace('themeRef_', '');
                });
            });
            return styleResult;
        };

        result = {
            ...result,
            ...getTemplateReferencesForStyle('normalStyle'),
            ...getTemplateReferencesForStyle('highlightStyle'),
        };

        return result;
    }

    #buildValueMapForStyle(valueMapMode = ValueMapMode.GET_REFERENCE_VALUES, style = '', onlyVisible = false) {
        // get dynamic styling properties for the style
        const result = {};
        this[style].colorGraphics.forEach((graphic, index) => {
            const attrKeyPrefix = `${style}.colorGraphics.${index}.shape`;
            const templateAttributes = getAllTemplateAttributes(graphic.shape, valueMapMode);
            templateAttributes.forEach(attr => {
                const attrKey = `${attrKeyPrefix}.${attr}`;
                if (isTemplateReference(graphic.shape[attr])) {
                    result[attrKey] = graphic.shape[`_${attr}Value`].replace('themeRef_', '');
                }
                else {
                    if (!(onlyVisible && attr === 'strokeColor' && graphic.shape['strokeWidth'] === 0)) {
                        result[attrKey] = graphic.shape[attr];
                    }
                }
            });
        });
        return result;
    }

    override buildColorGraphicsMap() {
        let result = super.buildColorGraphicsMap();
        const colorMap = {};
        result = {
            ...result,
            ...this.#buildValueMapForStyle(ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE, 'normalStyle', true),
            ...this.#buildValueMapForStyle(ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE, 'highlightStyle', true),
        };

        Object.entries(result).map(([valueKey, value]) => {
            if (valueKey.toLowerCase().includes('color') && valueKey.toLowerCase().includes('alpha') === false) {
                colorMap[valueKey] = value;
            }
        });
        return colorMap;
    }

    /**
     * Creates HTML nodes used for user interaction to update this text asset's text property. The element
     * gets its default styling, positioning, and event handlers configured here. For each change, the element's
     * children are rebuilt and repositioned to ensure alignment.
     */
    enableTextInput(domContainer: HTMLElement, options?: TextEditingOptions) {
        if (this.hasTextEditingEnabled) {
            return this.#textEditingManager.textInput;
        }
        try {
            this.#textEditingManager = new TextEditingManager(this, domContainer);
            this.requiresRedraw = true;

            if (options) {
                this.textEditingOptions = options;
            }
        }
        catch (e) {
            // if something fails, make sure the texteditingmanager is destroyed
            if (this.#textEditingManager) {
                this.#textEditingManager.destroy();
            }
            this.#textEditingManager = null;
            throw (e);
        }
    }

    /**
     * @internal
     */
    get _textEditingManager(): TextEditingManager {
        return this.#textEditingManager;
    }

    /**
     * Whether or not text editing mode is enabled
     */
    get hasTextEditingEnabled(): boolean {
        return this.#textEditingManager !== null;
    }

    /**
     * If the user is editing text, whether or not the text asset has no text value and
     * is showing a special empty state
     */
    get inEmptyState(): boolean {
        return this.hasTextEditingEnabled ?
            this.#textEditingManager.isShowingEmptyState : false;
    }

    /**
     * If the user is editing text, whether or not the last change they made was too large
     * for this text asset to fully display
     */
    get inOverflowState(): boolean {
        return this.hasTextEditingEnabled ?
            this.#textEditingManager.inOverflowState : false;
    }

    /**
     * The internal list of textLines, only supplied when a text asset is currently editing
     */
    get textLinesForEditing(): TextLine[] {
        if (this.hasTextEditingEnabled) {
            return this._textLines;
        }
        else {
            throw new Error('textLines cannot be accessed when not editing.');
        }
    }

    /**
     * Return text editing options for text asset
     */
    get textEditingOptions(): TextEditingOptions {
        return this._textEditingOptions;
    }

    /**
     * Indicates if word wrap should be used
     */
    get wordWrap(): boolean {
        return this.#wordWrap;
    }

    set wordWrap(value: boolean) {
        assertBoolean(value);
        if (this.#wordWrap !== value) {
            this.#wordWrap = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * The width at which text will wrap, it needs `wordWrap` to be set to true
     */
    get wordWrapWidth(): number {
        return this.#wordWrapWidth;
    }

    set wordWrapWidth(value: number) {
        assertNumber(value);
        assertMinimum(value, 0);
        if (this.#wordWrapWidth !== value) {
            this.#wordWrapWidth = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Set text editing options
     */
    set textEditingOptions(options: TextEditingOptions) {
        this._textEditingOptions = {
            ...DefaultTextEditingOptions,
            ...options,
        };
    }

    /**
     * Returns information used to force the window selection to
     * a default state when a user starts to edit text. If no coordinate is provided,
     * then this method assumes the cursor position will be at the very beginning
     *
     * @param x x-axis click coord, relative to luminary's resolution
     * @param y y-axis click coord, relative to luminary's resolution
     */
    getEditingCursorPosition(x = 0, y = 0): { node: Node; offset: number; } {
        if (!this.hasTextEditingEnabled) {
            return null;
        }
        assertNumberBetween(x, 0, this.root.referenceResolution.width);
        assertNumberBetween(y, 0, this.root.referenceResolution.height);

        return this.#textEditingManager.calculateCursorPos(x, y);
    }

    /**
     * If the user is editing the text asset, returns the current window selection as a tuple
     *
     * @return A tuple containing a start and an end index. If they are identical,
     *  that means the selection was collapsed
     */
    getTextInputSelection(): [number, number] {
        return this.hasTextEditingEnabled ? getCharRangeFromSelection() : null;
    }

    /**
     * Invokes the build process of the text editing manager. This method should be included
     * in any build loop so that the text editing input stays up to date.
     */
    updateTextInput() {
        if (this.hasTextEditingEnabled) {
            try {
                // Here, animations have not been applied yet.
                // We call _seek() so that PIXI lines and words match
                // NTE text bounds, according to any animation that could be applied.
                // Animations can impact text position is some scene designs.
                // An example is when a constant offset is applied via an animation.
                const timeParent = this.subscene ?? this.slide;
                this._seek(timeParent._timeline.progress, SeekMode.ResolveWhenSingleFrame);
                this.#textEditingManager._build();
            }
            catch (e) {
                this.#textEditingManager.resetCalculatedState();
                this._handleEditingException(e);
            }
        }
    }

    /**
     * Disables text editing by removing the input element from the DOM and destroying its reference.
     */
    disableTextInput() {
        if (!this.hasTextEditingEnabled) {
            return;
        }
        this.#textEditingManager.destroy();
        this.#textEditingManager = null;

        // do not allow a final text value comprised only of whitespaces/newlines
        if (this.text.trim().length === 0) {
            this.text = '';
        }
        else {
            this.requiresRedraw = true;
        }
    }

    /**
     * Fires a custom web event notifying listeners that an unrecoverable error has occurred while
     * editing, logs the error, and exits edit mode
     */
    _handleEditingException(e: Error) {
        if (this.hasTextEditingEnabled) {
            const webEvent = new Event(TextEditingManager.NTE_EVENT_EDITING_EXCEPTION);
            this.#textEditingManager.domContainer.dispatchEvent(webEvent);
            this.disableTextInput();
        }
        console.error(e);
        Sentry.captureException(e);
    }

    /******************************
     * GETTERS & SETTERS FOR STYLES
     ******************************/

    /**
     * The horizontal alignment of the text. Note that the alignment is within the bounds of this
     * TextAsset. This property does not impact how multiple TextAssets are aligned within a group
     * (such as [AssetGroup]{@link Luminary.AssetGroup}).
     * This property affects text metrics.
     *
     * @type {Luminary.Config.Config.TEXT_ALIGN_HORIZONTAL}
     */
    get textAlign() {
        return this.normalStyle.textAlign;
    }

    set textAlign(value) {
        assertString(value);
        assertInEnum(value, Config.TEXT_ALIGN_HORIZONTAL);
        if (this.textAlign !== value) {
            this.normalStyle.textAlign = value;
            this.highlightStyle.textAlign = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * The list specifying which characters are highlighted.
     * This property affects text metrics.
     *
     *  @returns An array of tuples [startHighlightIdx, endHighlightIdx] where
     *  startHighlightIdx < endHighlightIdx
     */
    get textHighlights(): [number, number][] {
        return this.#highlightAreas;
    }

    set textHighlights(value: [number, number][]) {
        assertArray(value);
        if (!isEqual(this.#highlightAreas, value)) {
            this.#highlightAreas = value;
            this.requiresRedraw = true;
        }
    }

    get highlightedTextScale(): number {
        return this.#highlightedTextScale;
    }

    set highlightedTextScale(scale: number) {
        if (this.highlightedTextScale === scale) {
            return;
        }
        assertNumberBetween(scale, 0.01, Infinity);
        this.#highlightedTextScale = scale;
        this.requiresRedraw = true;
    }

    /**
     * The base value used to scale the font size.
     * This property affects text metrics.
     */
    get textSize(): number {
        return this.#textSize;
    }

    set textSize(value: number) {
        if (this.textSize === value) {
            return;
        }
        assertNumberBetween(value, 0, Infinity);
        this.#textSize = value;
        this.requiresRedraw = true;
    }

    /**
     * The pixel value of the leading (space between lines, not including the height of the font)
     */
    get leading(): number {
        return this.normalStyle.leading;
    }

    /**
     * The multiplier which acts on the localized font size to determine the absolute value
     * for leading. This property affects text metrics.
     */
    get leadingMultiplier(): number {
        return this.#leadingMultiplier;
    }

    set leadingMultiplier(value: number) {
        assertNumber(value);
        if (this.leadingMultiplier !== value) {
            this.#leadingMultiplier = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * The maximum height of the text as a percentage. This property affects text metrics.
     */
    get maxHeight(): number {
        return this.normalStyle.maxTextHeight;
    }

    set maxHeight(value: number) {
        assertNumber(value);
        assertMinimum(value, 0);
        if (this.maxHeight !== value) {
            const normalStyleAutoUpdate = this.normalStyle._autoUpdate;
            this.normalStyle._autoUpdate = false;
            this.normalStyle.maxTextHeight = value;
            this.normalStyle._autoUpdate = normalStyleAutoUpdate;
            const highlightStyleAutoUpdate = this.highlightStyle._autoUpdate;
            this.highlightStyle._autoUpdate = false;
            this.highlightStyle.maxTextHeight = value;
            this.highlightStyle._autoUpdate = highlightStyleAutoUpdate;
            this.requiresRedraw = true;
        }
    }

    /**
     * The direction in which the text is read. This property affects text metrics.
     *
     * @returns {Luminary.Config.Config.READING_DIRECTION}
     */
    get readingDirection() {
        return this.normalStyle.readingDirection;
    }

    set readingDirection(value) {
        if (this.normalStyle.readingDirection !== value) {
            this.normalStyle.readingDirection = value;
            this.highlightStyle.readingDirection = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * The maximum width of the text as a percent. If set, this automatically enables word wrapping.
     * This property affects text metrics.
     */
    get maxWidth(): number {
        return this.wordWrapWidth;
    }

    set maxWidth(value: number) {
        assertNumber(value);
        assertMinimum(value, 0);
        if (this.maxWidth !== value) {
            this.wordWrapWidth = value;
            this.wordWrap = true;
            this.requiresRedraw = true;
        }
    }

    /****************************************
     * GETTERS & SETTERS FOR OTHER PROPERTIES
     ****************************************/

    /**
     * True when we have all the necessary fonts to enable to rendering of our text
     */
    override get isDoneTryingToLoad(): boolean {
        if (this.skipBuildAndDraw) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        if (this.hasAllNecessaryFonts) {
            return true;
        }
        if (!this.#fontManager.loadedAllFallbackFonts()) {
            // Fallback fonts are still loading, we may be able to render some more text once these load.
            return false;
        }

        if (this.#stylesList.every(style => this.#fontManager.getFontBySourceIfLoaded(style.fontUrl))) {
            // our text contains characters our fallback fonts don't support
            // but we don't have any other fonts to load
            return true;
        }
        return false;
    }

    /**
     * True when we have all the necessary fonts to enable to rendering of our text with or without using fallback fonts.
     */
    get hasAllNecessaryFonts(): boolean {
        return uniq(this.#stylesList.map(style => style.fontUrl))
            .every(fontUrl => this.#fontManager.hasAllNecessaryFonts(this.text, fontUrl));
    }

    /**
     * Returns information about any unsupported text in the string.
     * The caller must verify that all asset fonts have loaded with isLoaded(), otherwise this method will detect
     * all text as unsupported.
     */
    findUnsupportedText(): UnsupportedFontInfo[] {
        if (!this.hasText) {
            return [];
        }

        const unsupportedFontInfo: UnsupportedFontInfo[] = [];
        const allFonts = new Set<string>();
        this.#stylesList.forEach(style => allFonts.add(style.fontUrl));

        allFonts.forEach((fontUrl: string) => {
            const font = this.#fontManager.getFontBySource(fontUrl);
            if (!font) {
                // Without a font, we can't render anything.
                return this.text;
            }
            const { missing } = font.canRenderText(this.text);

            if (missing.length === 0) {
                return;
            }

            const fontName = this.#fontManager.getFullNameOfLoadedFont(fontUrl);

            unsupportedFontInfo.push({
                fontName,
                fontUrl,
                missing,
            });
        });

        return unsupportedFontInfo;
    }

    /**
     * Checks to see if a fallback url is used to render the current text
     */
    #isFallbackFontUsed(fallbackUrl: string): boolean {
        if (!this.#fontManager.isFallbackUrl(fallbackUrl)) {
            return false;
        }
        const fallbackFont = this.#fontManager.getFontBySourceIfLoaded(fallbackUrl);
        if (!fallbackFont) {
            return false;
        }

        const allFonts = new Set<string>();
        this.#stylesList.forEach(style => allFonts.add(style.fontUrl));

        return Array.from(allFonts).some(fontUrl => {
            const before = this.#fontManager.canRenderText(this.text, fontUrl, [fallbackUrl]);
            if (before.supported) {
                return false;
            }
            const after = this.#fontManager.canRenderText(before.missing, fontUrl);
            if (after.missing.length < before.missing.length) {
                return true;
            }
        });
    }

    /**
     * Reports loaded once the font has loaded and the font family is updated.
     */
    override get isLoaded(): boolean {
        if (this.skipBuildAndDraw) {
            return true;
        }
        // Note that we do need the fonts when editing the text even if there is no text. In fact, when displaying
        // the empty state, we will set some invisible text to have a proper sizing for the empty text box.
        if (!this.hasText && !this.hasTextEditingEnabled) {
            return true;
        }

        return this.#stylesList.every(style => !!this.#fontManager.getFontBySourceIfLoaded(style.fontUrl));
    }

    override async _bufferAtTime(_time: number, _bufferingMode: BufferingMode): Promise<void> {
        // nothing to buffer specifically, just make sure the asset has loaded
        await this.onLoaded();
    }

    async onLoaded(): Promise<void> {
        if (this.isLoaded || this.destroyed) {
            return;
        }
        return new Promise(resolve => {
            // for TextAsset canPlay === loaded
            this.once('assetCanPlay', resolve);
        });
    }

    _redrawOnFontLoad(sourceUrl: string) {
        // Our text can contain characters that our fontUrl doesn't support, which forces us to load fallback fonts
        // hoping that one of the loaded fonts contains the necessary glyphs. That's why we need to listen for more
        // than just the set fontUrl.
        if (this.normalStyle.fontUrl === sourceUrl) {
            this.requiresRedraw = true;
        }
        else if (this.highlightStyle.fontUrl === sourceUrl) {
            this.requiresRedraw = true;
        }
        else if (this.#isFallbackFontUsed(sourceUrl)) {
            this.requiresRedraw = true;
        }
        else if (!this.hasAllNecessaryFonts && this.#fontManager.loadedAllFallbackFonts()) {
            // All fallback fonts have been loaded but the text still cannot be drawn, so do one
            // more redraw in order to notify parents
            this.requiresRedraw = true;
        }
    }

    async _loadAllNecessaryFonts() {
        if (this.shouldLoad) {
            return Promise.all(this.#stylesList.map(async style => this.#fontManager.loadAllNecessaryFonts(this.text, style.fontUrl)));
        }
    }

    /**
     * Reports build once the font has been loaded, the family is updated AND the text has been built.
     */
    get isBuilt(): boolean {
        return this.isLoaded && !!this._moveContainer && !!this.textMetrics;
    }

    /**
     * Check if `fonts` is explicitly disabled in fetchResources
     */
    override get shouldLoad() {
        if (typeof this.fetchResources !== 'boolean') {
            return this.fetchResources && this.fetchResources.fonts;
        }
        return super.shouldLoad;
    }

    /**
     * Reports as failed if the font failed to load.
     */
    override get failedToLoad(): boolean {
        if (this.skipBuildAndDraw) {
            return false;
        }
        if (!this.hasText && !this.hasTextEditingEnabled) {
            return false;
        }
        const fonts = this.#stylesList.map(style => this.#fontManager.getFontBySource(style.fontUrl));
        if (fonts.some(font => !font)) {
            return true;
        }
        return fonts.some(font => font.failedToLoad);
    }

    /**
     * The text string to render. This affects text metrics.
     */
    get text(): string {
        return this.#text;
    }

    set text(text: string) {
        assertString(text);
        const displayText = unicodeToChar(text);
        if (this.#text !== displayText) {
            this.#text = displayText;
            this.requiresRedraw = true;
        }
    }

    override get canPlay(): boolean {
        if (!this.hasText) {
            return true;
        }
        return super.canPlay;
    }

    /**
     * Holds dimensional data for all children (all lines and words).
     */
    get textMetrics(): TextAssetMetrics {
        return this.#textMetrics;
    }

    /**
     * A scale factor used to help size text for different aspect ratios. This affects
     * text metrics because it changes the drawn font size and leading.
     */
    get textScaleFactor(): number {
        return this.#textScaleFactor;
    }

    set textScaleFactor(value: number) {
        if (this.textScaleFactor !== value) {
            this.#textScaleFactor = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Returns map of styles by name, currently normalStyle and highlightStyle
     */
    get _styles() {
        return this.#styles;
    }

    /**
     * Returns all list of styles, currently normalStyle and highlightStyle
     */
    get #stylesList(): TextAssetStyle[] {
        return Object.values(this._styles);
    }

    /**
     * The primary text styling.
     */
    get normalStyle(): TextAssetStyle {
        return this.#styles.normalStyle;
    }

    /**
     * The styling used for highlights.
     */
    get highlightStyle(): TextAssetStyle {
        return this.#styles.highlightStyle;
    }

    /**
     * Returns the approximate biggest possible bounds. As an approximation, it does not take into account
     * scenarios where the TextAsset is part of an AssetGroup and its maximum height is controlled by
     * the group
     * Each property is a number in the same units
     *  as luminary's resolution
     */
    override get approximateMaxBounds(): PIXI.Rectangle {
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = getPositionFromTransformation(transformation, this.maxWidth, this.maxHeight);
        return new PIXI.Rectangle(x, y, this.maxWidth, this.maxHeight);
    }

    /****************
     * PUBLIC METHODS
     ****************/

    /**
     * Destroys this text asset
     */
    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.normalStyle.destroy();
        this.highlightStyle.destroy();
        if (this.hasTextEditingEnabled) {
            this.disableTextInput();
        }
        this.#fontManager = null;
        this.#textMetrics = null;
        this.#wordElementDataStore.destroy();
        this._moveContainer = null;
        this._scaleContainer = null;
        this._negateScaleOffsetContainer = null;
        this._hierarchyContainer = null;
        this._layersContainer = null;
        this.#textLayerRenderTexture = null;
        this.#graphicsLayerRenderTexture = null;
        this.#textLines = [];
        this._highlightLines = [];
    }

    /**
     * Returns an object that includes the character index of the closest character to
     * the location passed in and the distance to that index
     */
    closestTextIndex(coordinate: Coordinate): Location {
        if (
            !coordinate ||
            typeof coordinate.x !== 'number' ||
            typeof coordinate.y !== 'number' ||
            coordinate.x < 0 ||
            coordinate.y < 0
        ) {
            return {
                index: 0,
                distance: Infinity,
            };
        }

        // if there's no text, put the cursor at the start
        if (this.#text.length === 0) {
            return {
                index: 0,
                distance: Infinity,
            };
        }

        // This factor allows the click to give preference to the current line, by virtually
        // separating lines from each other. This is obvious when an adjacent line is longer
        // than the current line. If a click happens behind the shorter line then the closest
        // char will be in the adjacent line, but it's more natural to put the cursor at the end
        // of the clicked line.
        const yScale = 10;
        coordinate.y *= yScale;

        let minDistance = Infinity;
        let indexToMinDistance = 0;
        const textAssetPosition = this.getGlobalPosition();

        // note that this loop goes from 0 to _after_ the last
        // character - we are checking each letter, before and after
        // so be careful not to trigger an index exception in here
        for (let i = 0; i <= this.#text.length; i++) {
            // get the line, word and character hierarchy index for the current character
            const offset = this.#textMetrics.getOffsetsForChar(i);

            // Make sure the PIXI objects actually exist
            if (offset.lineIndex >= this._wordElementSprites.length) {
                continue;
            }
            if (offset.wordIndex >= this._wordElementSprites[offset.lineIndex].length) {
                continue;
            }

            // get the position (Point) of the word the character belongs to
            const textLine = this._textLines[offset.lineIndex];
            const wordElementSprite = this._wordElementSprites[offset.lineIndex][offset.wordIndex][offset.elementIndex];

            // get the position (Point) of the character relative to the word
            const relCharacterPosition = this.textMetrics.positionOfCharRelativeToWordElement(
                offset.lineIndex, offset.wordIndex, offset.elementIndex, offset.characterIndex);

            // add the character offset to the position of the word to get the global character position
            const lineHeight = this.#textMetrics.getLineHeightFor(offset.lineIndex);
            const characterPosition = new PIXI.Point(
                textAssetPosition.x + wordElementSprite.x + relCharacterPosition.x,
                (textAssetPosition.y + textLine.y + lineHeight / 2) * yScale
            );

            // collect the index of the character closest to the pixel location
            const charDistance = distance(characterPosition, coordinate);
            if (charDistance < minDistance) {
                minDistance = charDistance;
                indexToMinDistance = i;
            }
        }

        return {
            index: indexToMinDistance,
            distance: minDistance,
        };
    }


    /**
     * Set the font size for the given text to fit within it's current boundary limits.
     */
    setFontSizeToFitAllText() {
        if (!this.canFitText(this.text)) {
            const MIN_SIZE_SHIFT = 5;
            const { baseFontSize } = this.getFontSizeToFitAllText(this.text, {
                max: this.textSize - MIN_SIZE_SHIFT,
                min: this.textEditingOptions.minSize,
            });
            this.textSize = baseFontSize;
        }
    }

    /**
     * Updates textSize if asset can fit text with the new text size, otherwise, sets inOverflowState to true.
     */
    tryToUpdateTextSize(value: number) {
        if (this.textSize === value) {
            return;
        }
        if (!this.hasTextEditingEnabled) {
            throw Error('Cannot call `tryToUpdateTextSize` outside of textEditing mode. Use `textSize` setter instead');
        }
        const canChangeFit = this.canFitText(this.text, {
            fontSize: value * this.textScaleFactor,
        });

        if (!canChangeFit) {
            this.#textEditingManager.inOverflowState = true;
            return;
        }
        else if (this.#textEditingManager.inOverflowState) {
            this.#textEditingManager.inOverflowState = false;
        }

        this.textSize = value;
    }

    /**
     * Returns the font size for the given text to fit within it's current boundary limits. Note
     * that the TextAsset does not have to be loaded to use this, however the global font loader
     * must have loaded the required fonts otherwise an Error will be thrown.
     *
     * @param text - the input string of text
     * @param {object} baseFontSizeRange - the valid range for the base font size
     * @param {number} baseFontSizeRange.min=50 - the min base font size
     * @param {number} baseFontSizeRange.max=250 - the max base font size
     */
    getFontSizeToFitAllText(text = 'My text', baseFontSizeRange = { min: 50, max: 250 }): FittedFontSize {
        const getTokensAndWordElementDataStore = (newNormalStyle: WordStyle, newHighlightStyle: WordStyle) => {
            const wordElementDataStore = new WordElementDataStore();
            const wordTokens = getTokenList(text, newNormalStyle, this.#highlightAreas, newHighlightStyle);
            wordTokens.forEach(wordToken => { wordElementDataStore.addItems(wordToken.wordElementTokens); });
            return { wordTokens, wordElementDataStore };
        };

        const minFontSize = baseFontSizeRange.min * this.textScaleFactor;
        const maxFontSize = baseFontSizeRange.max * this.textScaleFactor;

        // Because this api can be called externally, autoDraw may be disabled so ensure a sync
        this.#syncFontSizeAndLeadingToStyles();

        const newNormalStyle = this.normalStyle.createWordStyle();
        const newHighlightStyle = this.highlightStyle.createWordStyle();

        // reset the font size to its default value -- this ensures that text grows back to it's "default" size
        newNormalStyle.fontSize = maxFontSize;
        newNormalStyle.leading = maxFontSize * this.#leadingMultiplier;
        newHighlightStyle.fontSize = newNormalStyle.fontSize * this.highlightedTextScale;
        const { wordTokens, wordElementDataStore } = getTokensAndWordElementDataStore(newNormalStyle, newHighlightStyle);
        let textMetrics = TextAssetMetrics.create(wordTokens, this.wordWrap, this.maxWidth, wordElementDataStore);
        // make sure we don't do any extra work if we don't need to. If the text fits, exit
        if (textMetrics.canDisplayAllText) {
            return {
                fontSize: Math.round(newNormalStyle.fontSize),
                baseFontSize: Math.round(newNormalStyle.fontSize / this.textScaleFactor),
                displaysAllText: true,
            };
        }

        // make big jumps until we can fit the text
        let BIG_JUMP_DELTA = 20;
        if (newNormalStyle.fontSize <= BIG_JUMP_DELTA) {
            BIG_JUMP_DELTA = 10;
        }
        while (!textMetrics.canDisplayAllText && newNormalStyle.fontSize > minFontSize && newNormalStyle.fontSize > BIG_JUMP_DELTA) {
            // decrement font size
            newNormalStyle.fontSize -= BIG_JUMP_DELTA;
            if (newNormalStyle.fontSize <= minFontSize) {
                // we're smaller than the minFontSize – readjust to the minFontSize
                newNormalStyle.fontSize = minFontSize;
            }
            newHighlightStyle.fontSize = newNormalStyle.fontSize * this.highlightedTextScale;

            // calculate leading
            newNormalStyle.leading = newNormalStyle.fontSize * this.#leadingMultiplier;
            const { wordTokens, wordElementDataStore } = getTokensAndWordElementDataStore(newNormalStyle, newHighlightStyle);
            textMetrics = TextAssetMetrics.create(wordTokens, this.wordWrap, this.maxWidth, wordElementDataStore);
        }
        let displaysAllText = textMetrics.canDisplayAllText;

        // now make small changes until we have the perfect fit
        const SMALL_JUMP_DELTA = 3;
        if (displaysAllText) {
            while (textMetrics.canDisplayAllText) {
                newNormalStyle.fontSize += SMALL_JUMP_DELTA;
                newHighlightStyle.fontSize = newNormalStyle.fontSize * this.highlightedTextScale;
                newNormalStyle.leading = newNormalStyle.fontSize * this.#leadingMultiplier;
                const { wordTokens, wordElementDataStore } = getTokensAndWordElementDataStore(newNormalStyle, newHighlightStyle);
                textMetrics = TextAssetMetrics.create(wordTokens, this.wordWrap, this.maxWidth, wordElementDataStore);
            }
            newNormalStyle.fontSize -= SMALL_JUMP_DELTA;
            newHighlightStyle.fontSize = newNormalStyle.fontSize * this.highlightedTextScale;
            newNormalStyle.leading = newNormalStyle.fontSize * this.#leadingMultiplier;
        }

        // make sure our font is big enough
        if (newNormalStyle.fontSize < minFontSize) {
            newNormalStyle.fontSize = minFontSize;
            newHighlightStyle.fontSize = newNormalStyle.fontSize * this.highlightedTextScale;
            newNormalStyle.leading = newNormalStyle.fontSize * this.#leadingMultiplier;
            const { wordTokens, wordElementDataStore } = getTokensAndWordElementDataStore(newNormalStyle, newHighlightStyle);
            textMetrics = TextAssetMetrics.create(wordTokens, this.wordWrap, this.maxWidth, wordElementDataStore);
            displaysAllText = textMetrics.canDisplayAllText;
        }

        return {
            fontSize: Math.round(newNormalStyle.fontSize),
            baseFontSize: Math.round(newNormalStyle.fontSize / this.textScaleFactor),
            displaysAllText,
        };
    }

    /**
     * Builds the text objects: the text metrics, the texture hierarchy, etc.
     */
    _buildText() {
        if (this.destroyed) {
            return;
        }
        this.#syncFontSizeAndLeadingToStyles();
        this._computeTextMetrics();
        this._centerAnchor();
        this._buildTextureHierarchy();
    }

    /**
     * Creates a style with the color properties of the normal style, but the dimesional properties of the highlight
     * style, e.g. dropShadowDistance.
     */
    _createNormalHighlightStyle() {
        const style = this.highlightStyle.createWordStyle();

        if (this.normalStyle.renderHighlighted && this.normalStyle.renderHighlightedColor) {
            return style;
        }

        style.fill = this.normalStyle.textColor;
        style.dropShadowColor = this.normalStyle.textShadowColor;

        return style;
    }

    /**
     * re-align containers -- move the transform to the center so we can scale from the center instead of the left
     * top corner
     */
    _centerAnchor() {
        const textBounds = this.#textMetrics.getBounds();
        const halfWidth = textBounds.width / 2;
        const halfHeight = textBounds.height / 2;
        if (this.readingDirection === Config.READING_DIRECTION.LTR) {
            this._scaleContainer.position.set(halfWidth, halfHeight);
            this._negateScaleOffsetContainer.position.set(-halfWidth, -halfHeight);
        }
        else if (this.readingDirection === Config.READING_DIRECTION.RTL) {
            this._scaleContainer.position.set(0, halfHeight);
            this._negateScaleOffsetContainer.position.set(0, -halfHeight);
        }
    }

    /**
     * For the highlighted layer, the style is different to the normal layer.
     * The normal layer has both a normal style, and special 'normal highlighted' style for when `renderHighlighted` is
     * true.
     * Since the token is used both as part of a lookup key, as well as for the attached style, a unique token needs to
     * be created for use with the highlighted layer.
     */
    _createHighlightedWordElementToken(wordElementToken: WordElementToken, highlightWordStyle: WordStyle) {
        return new WordElementToken(
            wordElementToken.text,
            wordElementToken.highlighted,
            wordElementToken.font,
            highlightWordStyle
        );
    }

    /**
     * Aligns the text asset children to the render layers.
     * As the render layers are aligned to the top left of the screen, the top level container (_moveContainer) needs to
     * be offset to its global position in order to render in the correct location in the render layer texture.
     */
    override _seek(progress: number, seekMode: SeekMode) {
        super._seek(progress, seekMode);

        // Luminary created with `disableRenderer`.
        if (!this.root.renderer) {
            return;
        }

        if (!this.visible) {
            return;
        }

        // Note, I think this.destroyed should cover these other cases, but we're still getting errors in prod, so testing this for now
        if (this.destroyed || this._moveContainer.destroyed || this.#textLayerRenderTexture.destroyed || this.#graphicsLayerRenderTexture.destroyed) {
            return;
        }

        if (this.root._webGLContextLost) {
            return;
        }

        // There are a number of transforms that tweak the text asset elements. Re-align the render container to be sure
        // it is at the top left of the screen.
        this.#alignRenderContainer();
        this.#renderToLayerTextures();
    }

    #renderToLayerTextures() {
        // Use `alpha` vs `visible` for the _moveContainer visibility, otherwise bounds won't get calculated.
        this._moveContainer.alpha = 1.0;
        const position = this._moveContainer.position.clone();
        const globalPosition = this._moveContainer.getGlobalPosition();
        this._moveContainer.position = (globalPosition as PIXI.ObservablePoint);
        // On entry, both graphics and other scene elements are visible.
        this._setGraphicsVisibleState(false);
        const referenceResolution = this.root.referenceResolution;
        const outputResolution = this.root.outputResolution;
        const renderTextureScale = {
            x: outputResolution.width / referenceResolution.width,
            y: outputResolution.height / referenceResolution.height,
        };
        const matrix = new PIXI.Matrix().scale(renderTextureScale.x, renderTextureScale.y);
        try {
            this.root.renderer.render(this._moveContainer, {
                renderTexture: this.#textLayerRenderTexture,
                transform: matrix,
            });
        }
        catch (e) {
            Sentry.setContext('renderToLayerTextures', {
                text: this.text,
                moveContainer: !!this._moveContainer,
                moveContainerDestroyed: !!this._moveContainer?.destroyed,
                renderTexture: !!this.#textLayerRenderTexture,
                renderTextureDestroyed: !!this.#textLayerRenderTexture?.destroyed,
                renderTextureValid: !!this.#textLayerRenderTexture?.valid,
                renderTextureBase: !!this.#textLayerRenderTexture?.baseTexture,
                renderTextureBaseValid: !!this.#textLayerRenderTexture?.baseTexture?.valid,
                transform: matrix.toArray(false),
            });
            throw e;
        }
        this._setTextElementsVisibleState(false);
        this._setGraphicsVisibleState(true);
        this.root.renderer.render(this._moveContainer, {
            renderTexture: this.#graphicsLayerRenderTexture,
            transform: matrix,
        });
        this._setTextElementsVisibleState(true);
        // On exit, both graphics and other scene elements are visible - this is necessary for correct bounds
        // calculations, that make sure the bounds and anchor helpers have correct information for their display.
        this._moveContainer.position = position;
        this._moveContainer.alpha = 0.0;
    }

    /**
     * A pre-order walk over a node and its children.
     *
     * @param node The node to first visit. Its children are then visited.
     * @param visit Callback called when visited. Return true to indicate that the node is not a leaf node. If false, it
     * is a leaf node, and its children will not be visited.
     */
    _preOrderWalk(node: PIXI.DisplayObject, visit: (node: PIXI.DisplayObject) => boolean) {
        if (visit(node)) {
            if (node instanceof PIXI.Container) {
                node.children.forEach(child => {
                    this._preOrderWalk(child, visit);
                });
            }
        }
    }

    /**
     * Turns on or off the `visible` property of graphics.
     */
    _setGraphicsVisibleState(state: boolean) {
        // Masked graphics are ignored - it they are set to invisible, then the entire masked object disappears.
        const graphicNodeClassTypes = new Set(this.allColorGraphics.flatMap(configurator => configurator.supportedShapeClassTypes));
        this.allColorGraphics.flatMap(configurator => configurator._graphics).forEach(graphic => {
            const classType = graphic['classType'];
            if (graphicNodeClassTypes.has(classType) && !graphic.isMask) {
                graphic.visible = state;
            }
        });
    }

    /**
     * Turns on or off the `visible` property of certain nodes that are contain text.
     */
    _setTextElementsVisibleState(state: boolean) {
        const graphicNodeClassTypes = new Set(this.allColorGraphics.flatMap(configurator => configurator.supportedShapeClassTypes));
        const leafNodeClassTypes = new Set([...graphicNodeClassTypes, 'TextWord', 'HighlightSpan']);

        this._preOrderWalk(this._hierarchyContainer as any as PIXI.DisplayObject, node => {
            const classType = node['classType'];
            if (classType && !graphicNodeClassTypes.has(classType) && leafNodeClassTypes.has(classType)) {
                node.visible = state;
            }
            // Nodes with no classType are considered internal nodes.
            return !classType || !leafNodeClassTypes.has(classType);
        });
    }

    /**
     * create text hierarchy
     * +- line containers (so we can animate individual lines)
     *  +- word containers (so we can animate individual words)
     *   +- element containers (so we can assign different styles/colors to an # of characters)
     * +- highlight line containers
     *   +- highlighted span
     */
    _buildTextureHierarchy() {
        const createHighlightSpan = (x, y, isPlaceholder = false) => {
            const span = new HighlightSpan(isPlaceholder);
            span.x = x;
            span.y = y;
            span.alpha = this.highlightStyle.renderHighlighted ? 1 : 0;
            return span;
        };

        this.#textMetrics.lines.forEach((lineMetrics, index) => {
            // create/setup the text line container.
            const textLineContainer = new TextLine(lineMetrics, this);
            textLineContainer.name = `textLineContainer_${index}`;
            this._hierarchyContainer._addInternalChild(textLineContainer);

            // create/setup the highlight line container.
            const highlightLineContainer = new HighlightLine(lineMetrics);
            highlightLineContainer.name = `highlightLineContainer_${index}`;
            this._hierarchyContainer._addInternalChild(highlightLineContainer);

            this._textLines.push(textLineContainer);
            this._highlightLines.push(highlightLineContainer);

            // Create a mask container to optionally contain any objects requiring masking.
            // If an object needs to be in the line container but should not be masked,
            // that object can be moved up from the maskContainer to the textLineContainer.
            const maskContainer = new PIXI.Container();
            textLineContainer.addChild(maskContainer);
            textLineContainer.y = lineMetrics.y;
            highlightLineContainer.y = lineMetrics.y;

            // Pass 1: The normal layer.

            lineMetrics.textWords.forEach(textWordMetrics => {
                const textWordContainer = new TextWord(textWordMetrics);
                textWordContainer.name = `textWordContainer_${index}`;
                textLineContainer.addChild(textWordContainer);

                textWordMetrics.wordElements.forEach(wordElementMetrics => {
                    const wordElementToken = wordElementMetrics.wordElementToken;
                    if (wordElementToken === null) {
                        throw new Error('WordElementToken cannot be null');
                    }

                    const wordElementSprite = this.#wordElementDataStore.getNextWordElementSprite(wordElementToken);

                    if (wordElementToken.highlighted) {
                        // Alpha 0 use used when rendering 'gaps' in the normal text layer. The gaps are filled by the
                        // highlighted text.
                        // We can't simply remove the sprites as they are used in the `closestTextIndex()` method.
                        // When `normalStyle.renderHighlighted` is true there are no gaps - these sprites are rendered with a
                        // special 'normal highlighted' style. See `_createNormalHighlightStyle`.
                        wordElementSprite.alpha = this.normalStyle.renderHighlighted ? 1 : 0;
                    }

                    wordElementSprite.x = wordElementMetrics.x;
                    // Since the line container was positioned, the element needs to be relative
                    wordElementSprite.y = wordElementMetrics.y - lineMetrics.y;

                    textWordContainer.addChild(wordElementSprite);
                });
            });

            // Pass 2: The highlighted layer.

            const removeTrailingSpaceSprite = highlightSpan => {
                const lastSprite = highlightSpan.children[highlightSpan.children.length - 1];
                if (lastSprite.metrics.getBounds().width === 0) {
                    lastSprite.parent.removeChild(lastSprite);
                }
            };

            // The current span to which highlighted sprites are added.
            // It is added to the `highlightLineContainer`.
            let highlightSpan = null;
            let placeholderSpan = null;
            // Used in the loop to create `WordElementToken` instances.
            const highlightWordStyle = this.highlightStyle.createWordStyle();
            lineMetrics.textWords.forEach(textWordMetrics => {
                textWordMetrics.wordElements.forEach(wordElementMetrics => {
                    const wordElementToken = wordElementMetrics.wordElementToken;
                    if (wordElementToken.highlighted) {
                        if (placeholderSpan) {
                            removeTrailingSpaceSprite(placeholderSpan);
                            highlightLineContainer.addChild(placeholderSpan);
                        }
                        placeholderSpan = null;
                        const highlightedToken = this._createHighlightedWordElementToken(wordElementToken, highlightWordStyle);
                        const highlightedSprite = this.#wordElementDataStore.getNextWordElementSprite(highlightedToken);
                        if (highlightedSprite) {
                            if (wordElementMetrics.getBounds().width === 0 && !highlightSpan) {
                                // Whitespace has zero dimension, don't add these as the first sprite to the span, it
                                // confuses graphic coordinates and looks weird.
                                return;
                            }
                            // Note the span is positioned on 'x' of the word, and 'y' of the line. Adjustments are made
                            // for the highlight x, y position relative to these coordinates.
                            highlightSpan = highlightSpan ?? createHighlightSpan(textWordMetrics.x, lineMetrics.y);
                            highlightedSprite.x = wordElementMetrics.x - highlightSpan.x;
                            highlightedSprite.y = wordElementMetrics.y - lineMetrics.y - highlightSpan.y;
                            highlightSpan.addChild(highlightedSprite);
                        }
                    }
                    else {
                        if (highlightSpan) {
                            removeTrailingSpaceSprite(highlightSpan);
                            highlightLineContainer.addChild(highlightSpan);
                        }
                        highlightSpan = null;
                        const placeholder = new WordElementPlaceholder(wordElementMetrics);
                        if (wordElementMetrics.getBounds().width === 0 && !placeholderSpan) {
                            return;
                        }
                        // Note the span is positioned on 'x' of the word, and 'y' of the line. Adjustments are made
                        // for the highlight x, y position relative to these coordinates.
                        placeholderSpan = placeholderSpan ?? createHighlightSpan(textWordMetrics.x, lineMetrics.y, true);
                        placeholder.x = wordElementMetrics.x - placeholderSpan.x;
                        placeholder.y = wordElementMetrics.y - lineMetrics.y - placeholderSpan.y;
                        placeholderSpan.addChild(placeholder);
                    }
                });
            });

            if (highlightSpan) {
                removeTrailingSpaceSprite(highlightSpan);
                highlightLineContainer.addChild(highlightSpan);
            }

            if (placeholderSpan) {
                highlightLineContainer.addChild(placeholderSpan);
            }
        });
    }

    get _textLines(): TextLine[] {
        return this.#textLines;
    }

    /**
     * @returns `TextWord` instances grouped by line. e.g. text._textWords[lineIndex][wordIndex].
     */
    get _textWords(): TextWord[][] {
        if (this._cachedTextWords) {
            return this._cachedTextWords;
        }
        this._cachedTextWords = this._textLines.map(textLine => textLine.textWords);
        return this._cachedTextWords;
    }

    /**
     * @returns `HighlightSpan` instances grouped by line. e.g. text._highlightSpans[lineIndex][spanIndex].
     */
    get _highlightSpans(): HighlightSpan[][] {
        if (this._cachedHighlightSpans) {
            return this._cachedHighlightSpans;
        }
        this._cachedHighlightSpans = this._highlightLines.map(highlightLine => highlightLine.highlightSpans);
        return this._cachedHighlightSpans;
    }

    /**
     * @returns {WordElementSprite[][][]} `WordElementSprite` instances grouped by their word, which are grouped in
     * words per line. e.g. text._wordElementSprites[lineIndex][wordIndex][spriteIndex].
     */
    get _wordElementSprites() {
        if (this._cachedWordElementSprites) {
            return this._cachedWordElementSprites;
        }
        this._cachedWordElementSprites = this._textWords.map(line => line.map(word => word.wordElementSprites));
        return this._cachedWordElementSprites;
    }

    /**
     * Request text metrics to be updated. Once they're up to date then this will
     * emit a loaded and allow playback event and forces a redraw if new metrics were computed.
     * If a new update starts, any previous in progress updates won't save or emit events.
     */
    _computeTextMetrics() {
        // reset WordElementMetrics use counter to keep track of metrics used while building the current text
        this.#wordElementDataStore.resetCount();

        const normalHighlightStyle = this._createNormalHighlightStyle();

        // split text in WordTokens (which are divided in WordElementTokens)
        const wordTokens = getTokenList(this.#text, this.normalStyle.createWordStyle(), this.#highlightAreas, normalHighlightStyle);

        // create a WordElementMetrics for each unique token
        wordTokens.forEach(wordToken => { this.#wordElementDataStore.addItems(wordToken.wordElementTokens); });

        // lay out WordElementMetrics to match tokens
        // Note that this potentialy splits tokens to wrap lines
        this.#textMetrics = TextAssetMetrics.create(
            wordTokens,
            this.wordWrap,
            this.maxWidth,
            this.#wordElementDataStore
        );

        // Populate cache with highlighted tokens
        const highlightWordStyle = this.highlightStyle.createWordStyle();
        this.#textMetrics.lines.forEach((lineMetrics) => {
            lineMetrics.textWords.forEach(textWordMetrics => {
                textWordMetrics.wordElements.forEach(wordElementMetrics => {
                    const wordElementToken = wordElementMetrics.wordElementToken;
                    if (wordElementToken === null) {
                        throw new Error('WordElementToken cannot be null');
                    }

                    if (wordElementToken.highlighted) {
                        const highlightedStyleToken = this._createHighlightedWordElementToken(
                            wordElementToken, highlightWordStyle
                        );
                        this.#wordElementDataStore.addItem(highlightedStyleToken);
                    }
                });
            });
        });

        // remove all WordElementMetrics which were not needed to build the TextAssetMetrics
        this.#wordElementDataStore.prune();

        // Generate all required WordElementSprites
        this.#wordElementDataStore.generateRequiredSprites();
    }

    /**
     * Determines if an arbitrary text value and optionally a set of different styles
     * can be fully displayed or not
     */
    canFitText(newText: string = '', newStyles: TextEditingStyles = {}): boolean {
        // Because this api can be called externally, autoDraw may be disabled so ensure a sync
        this.#syncFontSizeAndLeadingToStyles();

        // reset WordElementMetrics use counter to keep track of metrics used while building the current text
        this.#wordElementDataStore.resetCount();

        const newFontSize = newStyles.fontSize || this.normalStyle.fontSize;
        const normalWordStyle = this.normalStyle.createWordStyle();
        const highlightWordStyle = this.highlightStyle.createWordStyle();

        // Update BOTH the font size and leading properties if the font size is updated
        if (newFontSize !== this.normalStyle.fontSize) {
            normalWordStyle.fontSize = newFontSize;
            highlightWordStyle.fontSize = newFontSize * this.highlightedTextScale;

            const leading = newFontSize * this.#leadingMultiplier;
            normalWordStyle.leading = leading;
            highlightWordStyle.leading = leading;
        }

        const wordTokens = getTokenList(newText, normalWordStyle, this.#highlightAreas, highlightWordStyle);
        wordTokens.forEach((wordToken) => {
            this.#wordElementDataStore.addItems(wordToken.wordElementTokens);
        });

        const metrics = TextAssetMetrics.create(
            wordTokens,
            this.wordWrap,
            this.maxWidth,
            this.#wordElementDataStore
        );
        return metrics.canDisplayAllText;
    }

    override clear() {
        if (this.destroyed) {
            return;
        }
        // Unparent word elements
        // (we might reuse word element, so we don't want to destroy them yet)
        this._wordElementSprites.flat(3).forEach((wordElementSprite) => {
            if (wordElementSprite.parent !== null) {
                wordElementSprite.parent.removeChild(wordElementSprite);
            }
        });
        this._highlightSpans.flat().forEach(span => {
            while (span.children.length > 0) {
                const child = span.children[0];
                child.parent.removeChild(child);
            }
        });
        // Destroy the rest of the hierarchy
        while (this._hierarchyContainer.children.length > 0) {
            this._hierarchyContainer.children[0].destroy(true);
        }

        this.resetTransformation();

        this.#textLines = [];
        this._highlightLines = [];

        this._cachedTextWords = null;
        this._cachedWordElementSprites = null;
        this._cachedHighlightSpans = null;
    }

    #syncProperties() {
        this.alpha = this.normalStyle.textAlpha;
    }

    override _build() {
        this.#syncProperties();

        this.clear();
        if (!this.hasText) {
            this._emitAssetCanPlay(); // no text = can play!
            return;
        }
        void this._loadAllNecessaryFonts();
        if (!this.isDoneTryingToLoad) {
            // font hasn't loaded, make sure we send the canPlay event when it does load
            this.sentCanPlay = false;
            return;
        }
        this._emitAssetCanPlay(); // done trying to loaded = can play!
        this._buildText();

        this._prepareDynamicStylingForUpdate();
        this.applyTransformation();
        this._updateDynamicStyling();
        this.normalStyle._updateDynamicStyling();
        this.highlightStyle._updateDynamicStyling();

        this.applyIntroAnimations();
        this.applyOutroAnimations();

        this.animationManager.setInitValues();
    }

    override get boundsWithoutAnimation() {
        if (this.hasText) {
            return super.boundsWithoutAnimation;
        }
        // The bounds will be an empty rectangle centered at (0,0) if the text is empty.
        // But returning a rectangle that's centered according to our transformation will help
        // place NTE's empty state box in a more accurate place on screen.
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = transformation.position;
        return new PIXI.Rectangle(x, y, 0, 0);
    }

    override _draw() {
        // pass
    }

    /**
     * Various transforms on the TextAsset (e.g. AssetGroup aligning text) will move the render container. This method
     * resets the render container to the top left of the screen.
     */
    #alignRenderContainer() {
        const point = this._layersContainer.getGlobalPosition();
        this._layersContainer.position.x -= point.x;
        this._layersContainer.position.y -= point.y;
    }

    /*****************
     * PRIVATE METHODS
     *****************/

    get hasText() {
        return this.text.trim().length !== 0;
    }

    /**
     * Scales the textSize and leading based on the text scale factor and then syncs it to the
     * two different styles.
     */
    #syncFontSizeAndLeadingToStyles() {
        const scaledFontSize = this.textSize * this.textScaleFactor;
        this.normalStyle.fontSize = scaledFontSize;
        this.highlightStyle.fontSize = scaledFontSize * this.highlightedTextScale;

        // set leading based on the scaled textSize
        const leading = scaledFontSize * this.#leadingMultiplier;
        this.normalStyle.leading = leading;
        this.highlightStyle.leading = leading;
    }

    override get propertiesToSerialize() {
        // Dynamic Styling properties will be saved in normalStyle/highlightStyle
        return super.propertiesToSerialize.concat([
            'maxWidth',
            'maxHeight',
            'textAlign',
            'textScaleFactor',
            'leadingMultiplier',
            'highlightedTextScale',
            'visible',
        ]);
    }

    /**
     * List of element properties that can be edited by users and would need to be preserved between
     * aspect ratio changes.
     */
    override get editableProperties() {
        const dynamicStylingProperties = this.dynamicStylingPropertiesToSerialize;
        return super.editableProperties.concat([
            'text',
            'textHighlights',
            'textSize',
            'readingDirection',
            'normalStyle',
            'highlightStyle',
        ]).filter((property) => !dynamicStylingProperties.includes(property) || property === 'animateOutBehaviour');
    }

    /**
     * Ensures the text highlight areas are correct
     */
    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        this.#sanitizeHighlights();
        const isRTL = this.readingDirection === Config.RIGHT_TO_LEFT_TEXT_DIRECTION;
        this.colorGraphics.forEach((graphic) => { graphic._useLastElement = isRTL; });
        this.alphaGraphics.forEach((graphic) => { graphic._useLastElement = isRTL; });
        this.normalStyle?.colorGraphics.forEach((graphic) => { graphic._useLastElement = isRTL; });
        this.normalStyle?.alphaGraphics.forEach((graphic) => { graphic._useLastElement = isRTL; });
        this.highlightStyle?.colorGraphics.forEach((graphic) => { graphic._useLastElement = isRTL; });
        this.highlightStyle?.alphaGraphics.forEach((graphic) => { graphic._useLastElement = isRTL; });

        if (!this.hasTextEditingEnabled) {
            this.alpha = (this.text.trim().length === 0) ? 0 : 1;
        }
    }

    /**
     * @inheritdoc
     */
    override get allColorGraphics() {
        return this.colorGraphics
            .concat(this.normalStyle.colorGraphics)
            .concat(this.highlightStyle.colorGraphics);
    }

    /**
     * @override
     */
    override destroyDynamicStyles() {
        super.destroyDynamicStyles();
        this.normalStyle.destroyDynamicStyles();
        this.highlightStyle.destroyDynamicStyles();
    }

    // Temporary: editor has to be changed to edit normalStyle and highlightStyle
    override addColorGraphic(_properties): DynamicGraphicsConfigurator {
        throw new Error('Dynamic Styling must be added to the normal and highlight styles, not to the TextAsset');
    }

    override removeColorGraphic(_index) {
        throw new Error('Dynamic Styling must be added to the normal and highlight styles, not to the TextAsset');
    }

    override addAlphaGraphic(_properties): DynamicGraphicsConfigurator {
        throw new Error('Dynamic Styling must be added to the normal and highlight styles, not to the TextAsset');
    }

    override removeAlphaGraphic(_index) {
        throw new Error('Dynamic Styling must be added to the normal and highlight styles, not to the TextAsset');
    }

    override addObjectAnimation(): DynamicAnimationConfiguration {
        throw new Error('Dynamic Styling must be added to the normal and highlight styles, not to the TextAsset');
    }

    override removeObjectAnimation(_index) {
        throw new Error('Dynamic Styling must be added to the normal and highlight styles, not to the TextAsset');
    }

    /**
     * Ensures all highlights are in the valid range, merges any overlaps, and
     * sorts all highlights by their start index ascending
     */
    #sanitizeHighlights() {
        // discard any highlights with an invalid start index
        const filteredHighlights = this.#highlightAreas.filter((highlight) => {
            return highlight[0] < highlight[1]
                && highlight[0] >= 0
                && highlight[0] < this.text.length;
        });

        // for any highlight that exists past the text's length, cap it at the length
        const cappedHighlights: [number, number][] = filteredHighlights.map(highlight => {
            if (highlight[1] > this.text.length) {
                return [highlight[0], this.text.length];
            }
            return highlight;
        });

        // sort by start index ascending
        const sortedHighlights = cappedHighlights.sort((a, b) => a[0] - b[0]);

        // merge any overlapping highlights
        for (let i = 1; i < sortedHighlights.length; i++) {
            const firstHighlightIdx = i - 1;
            const firstHighlight = sortedHighlights[firstHighlightIdx];
            const secondHighlight = sortedHighlights[i];

            const hasOverlap = secondHighlight[0] <= firstHighlight[1]
                && secondHighlight[1] >= firstHighlight[0];
            if (!hasOverlap) {
                continue;
            }

            // replace the two compared highlights with a merged version
            const allIndices = [...firstHighlight, ...secondHighlight];
            const mergedHighlight: [number, number] = [Math.min(...allIndices), Math.max(...allIndices)];
            const spliceArgs: [number, number, [number, number]?] = [firstHighlightIdx, 2];
            if (mergedHighlight[1] - mergedHighlight[0] > 0) {
                spliceArgs.push(mergedHighlight); // the highlight must span at least one char
                i--;
            }
            sortedHighlights.splice(...spliceArgs);
        }

        this.#highlightAreas = sortedHighlights;
    }

    /**
     * @inheritdoc
     */
    override _prepareDynamicStylingForUpdate() {
        super._prepareDynamicStylingForUpdate();
        this.normalStyle?._prepareDynamicStylingForUpdate();
        this.highlightStyle?._prepareDynamicStylingForUpdate();
    }

    /**
     * Gets the creation containers for the normal style.
     */
    #getNormalStyleCreationContainersAtLevelForType = (level, type) => {
        assertInArray(level, Config.ALL_CREATION_LEVELS);
        let result = [];

        if (type === CreationType.Animation) {
            switch (level) {
                case DynamicStylingCreationLevel.Object:
                    result = [this._scaleContainer];
                    break;
                case DynamicStylingCreationLevel.Line:
                    result = this._textLines;
                    break;
                case DynamicStylingCreationLevel.Word:
                    result = this._textWords;
                    break;
                case DynamicStylingCreationLevel.Highlight:
                    result = this._highlightLines;
                    break;
            }
        }
        else {
            switch (level) {
                case DynamicStylingCreationLevel.Object:
                    result = [this._moveContainer];
                    break;
                case DynamicStylingCreationLevel.Line:
                    result = this._textLines;
                    break;
                case DynamicStylingCreationLevel.Word:
                    result = this._textWords;
                    break;
                case DynamicStylingCreationLevel.Highlight:
                    result = this._highlightLines;
                    break;
            }
        }
        return result;
    };

    #getHighlightStyleCreationContainersAtLevelForType = (level: DynamicStylingCreationLevel, type: CreationType) => {
        let result = [];
        switch (level) {
            case DynamicStylingCreationLevel.Line:
                if (type === CreationType.Animation) {
                    result = this._highlightLines;
                }
                break;
            case DynamicStylingCreationLevel.Highlight:
                result = this._highlightSpans;
                break;
        }
        return result;
    };

    /**
     * @inheritDoc
     */
    override getCreationContainersAtLevelForType(level: DynamicStylingCreationLevel, type: CreationType = CreationType.Animation): PIXI.Container[] {
        assertInArray(level, Config.ALL_CREATION_LEVELS);
        assertInEnum(type, CreationType);
        let result = [];
        if (level === DynamicStylingCreationLevel.Object) {
            result = result.concat(type === CreationType.Animation ? this._scaleContainer : this._moveContainer);
        }
        else {
            result = this.normalStyle.getCreationContainersAtLevelForType(level, type);
        }
        return result;
    }
}
